import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "@/lib/api";

const DEFAULTS = {
  brand_name: "DARA",
  tagline: "Enterprise AI Data Monetization",
  accent_color: "#FFB000",
  accent_text_color: "#000000",
  email_from_name: "",
  has_logo: false,
  logo_url: null,
  workspace_id: null,
};

const BrandKitContext = createContext({
  brand: DEFAULTS,
  loading: true,
  resolveBy: () => Promise.resolve(DEFAULTS),
});

/** Wraps the whole tree. Loads the authenticated user's brand kit if a token
 *  is present; otherwise serves DEFAULTS. Public pages (share/abm/deal-room)
 *  call `resolveBy(kind, slug)` to fetch the kit attached to that resource. */
export function BrandKitProvider({ children }) {
  const [brand, setBrand] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);

  const applyCss = useCallback((b) => {
    const root = document.documentElement;
    root.style.setProperty("--brand-accent", b.accent_color || "#FFB000");
    root.style.setProperty("--brand-accent-text", b.accent_text_color || "#000000");
  }, []);

  useEffect(() => {
    let alive = true;
    const t = localStorage.getItem("dara_token");
    if (!t) { setLoading(false); applyCss(DEFAULTS); return undefined; }
    axios.get(`${API}/brand-kit`, { headers: { Authorization: `Bearer ${t}` } })
      .then((r) => { if (alive) { setBrand({ ...DEFAULTS, ...r.data }); applyCss(r.data); } })
      .catch(() => { if (alive) applyCss(DEFAULTS); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [applyCss]);

  const refresh = useCallback(async () => {
    const t = localStorage.getItem("dara_token");
    if (!t) return DEFAULTS;
    try {
      const r = await axios.get(`${API}/brand-kit`, { headers: { Authorization: `Bearer ${t}` } });
      const merged = { ...DEFAULTS, ...r.data };
      setBrand(merged);
      applyCss(merged);
      return merged;
    } catch {
      return brand;
    }
  }, [brand, applyCss]);

  const resolveBy = useCallback(async (kind, slug) => {
    try {
      const r = await axios.get(`${API}/brand-kit/by-slug/${kind}/${encodeURIComponent(slug)}`);
      const merged = { ...DEFAULTS, ...r.data };
      setBrand(merged);
      applyCss(merged);
      return merged;
    } catch {
      return DEFAULTS;
    }
  }, [applyCss]);

  return (
    <BrandKitContext.Provider value={{ brand, loading, refresh, resolveBy }}>
      {children}
    </BrandKitContext.Provider>
  );
}

export function useBrand() {
  return useContext(BrandKitContext);
}

/** Compact "logo + brand_name" component used in every public/private header. */
export function BrandLogo({ size = "md", className = "" }) {
  const { brand } = useBrand();
  const heights = { sm: "h-5", md: "h-7", lg: "h-9" };
  const textSizes = { sm: "text-base", md: "text-lg", lg: "text-2xl" };
  const h = heights[size] || heights.md;
  const t = textSizes[size] || textSizes.md;
  if (brand.has_logo && brand.logo_url) {
    const src = brand.logo_url.startsWith("http") ? brand.logo_url : `${process.env.REACT_APP_BACKEND_URL}${brand.logo_url}`;
    return (
      <img
        src={src}
        alt={brand.brand_name}
        className={`${h} max-w-[180px] object-contain ${className}`}
        data-testid="brand-logo"
      />
    );
  }
  return (
    <div className={`flex items-center gap-2 ${className}`} data-testid="brand-logo-text">
      <span className="text-[#FFB000] font-bold">{">_"}</span>
      <span className={`font-heading font-black tracking-tight ${t}`}>{brand.brand_name}</span>
    </div>
  );
}
